import { api, apiBlobRequest, apiRequest } from "./api";

export const MAX_DOCUMENT_UPLOAD_BYTES = 20 * 1024 * 1024;
export const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
] as const;
export const DOCUMENT_ACCEPT =
  "application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg";

const ALLOWED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg"];

// Client-side courtesy check only. The server's byte sniffing is the authority
// (backend/internal/api/docs_upload_guard.go); this just saves a wasted upload
// and rate-limit slot. Wording matches the server messages.
export function preflightDocumentFile(file: {
  name: string;
  size: number;
  type: string;
}): string | null {
  if (file.size > MAX_DOCUMENT_UPLOAD_BYTES) {
    return "Document is larger than the 20 MB limit";
  }
  const typeOk = file.type
    ? (ALLOWED_DOCUMENT_TYPES as readonly string[]).includes(file.type)
    : ALLOWED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
  return typeOk ? null : "Upload a PDF, PNG, or JPEG document";
}

export type DocumentProcessingStatus =
  | "processing"
  | "ready_for_review"
  | "needs_attention";
export type DocumentReviewState =
  | "needs_review"
  | "partially_reviewed"
  | "reviewed";
export type DocumentReviewDecisionType = "accept" | "reject" | "edit" | "defer";

export interface DocumentUploadRecord {
  id: string;
  profile_id: string;
  uploaded_by: string;
  doc_type_hint?: string;
  storage_path: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  file_hash_sha256?: string;
  virus_scan_status: string;
  ingested_at: string;
}

export interface DocumentExtractionRun {
  id: string;
  upload_id: string;
  doc_type_detected: string;
  schema_version: string;
  extractor_name: string;
  extractor_version: string;
  ocr_provider: string;
  ocr_version: string;
  started_at: string;
  finished_at?: string;
  status: "running" | "succeeded" | "failed";
  error?: string;
}

export interface DocumentReviewFieldDecision {
  decision: DocumentReviewDecisionType;
  edited_value?: unknown;
  rationale?: string;
  reviewed_at: string;
}

export interface DocumentReviewField {
  observation_id?: string;
  field_key: string;
  entity_key: string;
  label: string;
  display_value?: string;
  normalized_value?: unknown;
  confidence: string;
  required: boolean;
  decision?: DocumentReviewFieldDecision | null;
}

export interface DocumentUploadSummary {
  upload: DocumentUploadRecord;
  latest_run?: DocumentExtractionRun | null;
  fields: DocumentReviewField[];
  processing_status: DocumentProcessingStatus;
  review_state: DocumentReviewState;
  contributes_to_verified_snapshot: boolean;
}

export interface DocumentUploadListResponse {
  uploads: DocumentUploadSummary[];
}

export interface UploadDocumentInput {
  docType: string;
  file: File;
}

export interface ReviewDocumentUploadRequest {
  decisions: Array<{
    field_key: string;
    observation_id: string;
    decision: DocumentReviewDecisionType;
    edited_value?: unknown;
    rationale?: string;
  }>;
}

export interface ExportReadinessPackageInput {
  selectedUploadIDs: string[];
}

export async function fetchDocumentUploads(
  token: string,
): Promise<DocumentUploadListResponse> {
  return api.get<DocumentUploadListResponse>("/api/docs/uploads", token);
}

export async function uploadDocument(
  input: UploadDocumentInput,
  token: string,
) {
  const body = new FormData();
  body.append("doc_type", input.docType);
  body.append("file", input.file);

  return apiRequest("/api/docs/uploads", {
    body,
    method: "POST",
    token,
  });
}

export async function reviewDocumentUpload(
  uploadID: string,
  body: ReviewDocumentUploadRequest,
  token: string,
) {
  return api.post(`/api/docs/uploads/${uploadID}/review`, body, token);
}

export async function deleteDocumentUpload(uploadID: string, token: string) {
  return api.delete(`/api/docs/uploads/${uploadID}`, token);
}

export async function exportReadinessPackage(
  input: ExportReadinessPackageInput,
  token: string,
) {
  const result = await apiBlobRequest("/api/docs/readiness-package/export", {
    body: { selected_upload_ids: input.selectedUploadIDs },
    method: "POST",
    token,
  });
  return {
    blob: result.blob,
    filename: result.filename ?? "sill-readiness-package.zip",
  };
}
